import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Badge,
} from "../../../components/ui";
import {
  fetchCrmRoles,
  fetchCrmUserRoles,
  createCrmUserRole,
  deleteCrmUserRole,
} from "../../../services/crmSettings";
import { getAuth, onAuthStateChanged } from "firebase/auth";

export default function CrmUserRolesSettings() {
  const [items, setItems] = useState([]);
  const [crmRoles, setCrmRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const [form, setForm] = useState({
    user_id: "",
    role_code: "",
    is_active: true,
  });

  const [authReady, setAuthReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!authReady || !isLoggedIn) return;
    load();
  }, [authReady, isLoggedIn]);

  async function load() {
    try {
      setLoading(true);
      setError("");

      const [userRoleRows, crmRoleRows] = await Promise.all([
        fetchCrmUserRoles(),
        fetchCrmRoles(),
      ]);

      setItems(Array.isArray(userRoleRows) ? userRoleRows : []);
      setCrmRoles(Array.isArray(crmRoleRows) ? crmRoleRows : []);
    } catch (err) {
      setError(err.message || "Failed to load CRM user roles");
    } finally {
      setLoading(false);
    }
  }

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;

    return items.filter((u) => {
      const display = (u.display_name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const roles = (u.roles || []).map((r) => (r.role_code || "").toLowerCase()).join(" ");
      return display.includes(q) || email.includes(q) || roles.includes(q);
    });
  }, [items, query]);

  async function handleAssign(e) {
    e.preventDefault();

    if (!form.user_id || !form.role_code) {
      setError("User and CRM role are required.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      await createCrmUserRole({
        user_id: Number(form.user_id),
        role_code: form.role_code,
        is_active: form.is_active ? 1 : 0,
      });

      await load();

      setForm({
        user_id: "",
        role_code: "",
        is_active: true,
      });
    } catch (err) {
      setError(err.message || "Failed to assign CRM role");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <Card className="xl:col-span-2">
        <CardHeader>
          <CardTitle>CRM User Roles</CardTitle>
          <CardDescription>
            Assign CRM business roles to real users from the system.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
              {error}
            </div>
          )}

          <div className="mb-4">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search users, emails or role codes..."
            />
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground">Loading...</div>
          ) : (
            <div className="space-y-2">
              {filteredItems.map((u) => (
                <div key={u.user_id} className="border rounded-lg px-4 py-3">
                  <div className="font-medium">
                    {u.display_name || u.email}
                  </div>

                  <div className="text-sm text-muted-foreground mt-1">
                    {u.email}
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3">
                    {(u.roles || []).length > 0 ? (
                      u.roles.map((r) => (
                        <div
                          key={r.id}
                          className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm"
                        >
                          <Badge variant="secondary">{r.role_code}</Badge>

                          <button
                            type="button"
                            className="text-red-600 text-xs"
                            onClick={async () => {
                              try {
                                await deleteCrmUserRole(r.id);
                                await load();
                              } catch (err) {
                                setError(err.message || "Failed to delete CRM user role");
                              }
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        No CRM roles assigned.
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {filteredItems.length === 0 && (
                <div className="text-sm text-muted-foreground">No users found.</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Assign CRM role</CardTitle>
          <CardDescription>Connect a user with a CRM business role.</CardDescription>
        </CardHeader>

        <CardContent>
          <form className="space-y-3" onSubmit={handleAssign}>
            <div>
              <div className="text-sm mb-1">User</div>
              <select
                className="h-10 w-full rounded-md border px-3 bg-white dark:bg-neutral-900"
                value={form.user_id}
                onChange={(e) => setForm((s) => ({ ...s, user_id: e.target.value }))}
              >
                <option value="">Select user...</option>
                {items.map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {(u.display_name || u.email)} — {u.email}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="text-sm mb-1">CRM role</div>
              <select
                className="h-10 w-full rounded-md border px-3 bg-white dark:bg-neutral-900"
                value={form.role_code}
                onChange={(e) => setForm((s) => ({ ...s, role_code: e.target.value }))}
              >
                <option value="">Select CRM role...</option>
                {crmRoles
                  .filter((r) => Number(r.is_active) === 1)
                  .map((r) => (
                    <option key={r.id} value={r.code}>
                      {r.label} ({r.code})
                    </option>
                  ))}
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
              />
              Active
            </label>

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : "Assign role"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}